import { computed } from 'mobx';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { Icons } from '../models/configuration/accessibility/icons';
import { Buttons } from '../models/configuration/accessibility/buttons';
import { Dropdowns } from '../models/configuration/accessibility/dropdowns';
import { Accessibility } from '../models/configuration/Accessibility';
import { DataStore } from './DataStore';
import { sentenceCase } from 'change-case';
import { Chart } from '../models/configuration/accessibility/chart';
import { Charts } from '../models/configuration/accessibility/charts';
import { RowExpander } from '../models/configuration/accessibility/rowExpander';
import { formatDateTime } from '../utils/formatter';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { translate } from '../Components/Translate';
import FormatDateOpitons from '../models/utils/FormatDateOptions';
import { Links } from '../models/configuration/accessibility/links';
import { Fact } from '../models/configuration/accessibility/fact';
import { AccessibilityStoreSearchKey } from '../models/configuration/accessibility/search';
import { Superscripts } from '../models/configuration/accessibility/superscripts';

export class AccessibilityStore extends BaseStore {
    dataStore: DataStore;

    constructor(stores: Stores) {
        super(stores);
        this.dataStore = stores.data;
    }

    @computed
    get hasAccessibility(): boolean {
        return this.accessibilityConfig !== undefined;
    }

    @computed
    get accessibilityConfig(): Accessibility {
        const { config } = this.dataStore;
        return (
            config && config.globalConfig && config.globalConfig.accessibility
        );
    }

    @computed
    get icons(): Icons {
        if (this.hasAccessibility) {
            const { icons } = this.accessibilityConfig;
            return icons;
        }
    }

    @computed
    get charts(): Charts {
        if (this.hasAccessibility) {
            return this.accessibilityConfig.charts;
        }
    }

    @computed
    get buttons(): Buttons {
        if (this.hasAccessibility) {
            const { buttons } = this.accessibilityConfig;
            return buttons;
        }
    }

    @computed
    get dropdowns(): Dropdowns {
        if (this.hasAccessibility) {
            const { dropdowns } = this.accessibilityConfig;
            return dropdowns;
        }
    }

    @computed
    get fact(): Fact {
        if (this.hasAccessibility) {
            const { fact } = this.accessibilityConfig;
            return fact;
        }
    }

    showSearchLabel(key: AccessibilityStoreSearchKey): boolean {
        if (this.hasAccessibility && this.accessibilityConfig[key]) {
            const { showLabel = false } = this.accessibilityConfig[key];
            return showLabel;
        }
        return false;
    }

    getDocumentSearchLabel(key: AccessibilityStoreSearchKey): string {
        if (this.hasAccessibility && this.accessibilityConfig[key]) {
            const { label } = this.accessibilityConfig[key];
            return label;
        }
    }

    @computed
    get rowExpander(): RowExpander {
        if (this.hasAccessibility) {
            return this.accessibilityConfig.rowExpander;
        }
    }

    @computed
    get rowExpanderColumnHeader() {
        return this.rowExpander && this.rowExpander.columnHeader;
    }

    @computed
    get mainLandmark(): string {
        let value = 'Explorer Content';
        if (this.hasAccessibility && this.accessibilityConfig.landmarks) {
            value = this.accessibilityConfig.landmarks.main;
        }
        return this.translateValue(value);
    }

    @computed
    get searchLandmark(): string {
        let value = 'Search';
        if (this.hasAccessibility && this.accessibilityConfig.landmarks) {
            value = this.accessibilityConfig.landmarks.search;
        }
        return this.translateValue(value);
    }

    @computed
    get filtersLandmark(): string {
        let value = 'Filters';
        if (this.hasAccessibility && this.accessibilityConfig.landmarks) {
            value = this.accessibilityConfig.landmarks.filters;
        }
        return this.translateValue(value);
    }

    @computed
    get modulesLandmark(): string {
        let value = 'Module Tabs';
        if (this.hasAccessibility && this.accessibilityConfig.landmarks) {
            value = this.accessibilityConfig.landmarks.modules;
        }
        return this.translateValue(value);
    }

    @computed
    get fundTypesLandmark(): string {
        let value = 'Fund Type Tabs';
        if (this.hasAccessibility && this.accessibilityConfig.landmarks) {
            value = this.accessibilityConfig.landmarks.fundTypes;
        }
        return this.translateValue(value);
    }

    @computed
    get links(): Links {
        if (this.hasAccessibility) {
            return this.accessibilityConfig.links;
        }
    }

    @computed
    get superscripts(): Superscripts {
        if (this.hasAccessibility) {
            return this.accessibilityConfig.superscripts;
        }
    }

    translateValue(value: string): string {
        return translate(this.stores.translation, value);
    }

    chart(chartId: string): Chart {
        if (this.charts && this.charts[chartId]) {
            return this.charts[chartId];
        }
    }

    buttonAccessibilityValue(
        buttonAccessibilityKey: keyof Buttons,
        defaultValue?: string,
        placeholders?: object
    ): string {
        const value =
            (this.buttons && this.buttons[buttonAccessibilityKey]) ||
            defaultValue;
        if (translate && this.stores.translation) {
            return this.stores.translation.translate(value, placeholders);
        }
    }

    dropdownAccessibilityValue(
        dropdownAccessibilityKey: string,
        defaultValue?: string,
        placeholders?: object
    ): string {
        const value =
            (this.dropdowns && this.dropdowns[dropdownAccessibilityKey]) ||
            defaultValue;
        if (translate && this.stores.translation) {
            return this.stores.translation.translate(value, placeholders);
        }
    }

    superscriptAccessibilityValue(
        key: keyof Superscripts,
        defaultValue: string,
        placeholders?: object
    ) {
        const value =
            (this.superscripts && this.superscripts[key]) || defaultValue;
        if (value) {
            if (this.stores.translation && this.stores.translation.translate) {
                return this.stores.translation.translate(value, placeholders);
            }
            return value;
        }
    }

    linkAccessibilityValue(
        linkAccessibilityKey: string,
        replacements?: object
    ): string {
        if (this.links && this.links[linkAccessibilityKey]) {
            let value = '';
            if (translate && this.stores.translation) {
                value = this.translateValue(this.links[linkAccessibilityKey]);
            }
            if (replacements) {
                value = replacePlaceholders({
                    text: value,
                    values: replacements,
                });
            }
            return value;
        }
    }

    factAccessibilityValue(factAccessibilityKey: string): string {
        if (this.fact) {
            if (this.fact[factAccessibilityKey]) {
                if (translate && this.stores.translation) {
                    return this.translateValue(this.fact[factAccessibilityKey]);
                }
            }
            return this.translateValue(this.fact.fact);
        }
        return this.translateValue('Fund Fact');
    }

    chartProperty(
        chartId: string,
        property: keyof Chart,
        replacements?: object
    ) {
        const defaultChart = this.chart('default');
        const chart = this.chart(chartId);
        let propertyValue = '';
        if (chart && chart[property] !== undefined) {
            propertyValue = chart[property];
        } else if (defaultChart && defaultChart[property] !== undefined) {
            propertyValue = defaultChart[property];
        }
        if (translate && this.stores.translation) {
            propertyValue = this.translateValue(propertyValue);
        }
        if (replacements) {
            propertyValue = replacePlaceholders({
                text: propertyValue,
                values: replacements,
            });
        }
        return propertyValue;
    }

    formatDate(date: string, locale: string) {
        const dateOptions: FormatDateOpitons = {
            locale,
            options: {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            },
            value: date,
        };
        return formatDateTime(dateOptions);
    }

    // Clean up file name so that it can be lined up to a key in the config
    removeFileNameExtension = (originalFileName: string): string => {
        if (
            typeof originalFileName !== 'string' ||
            originalFileName.length === 0
        ) {
            return undefined;
        }
        let fileName = originalFileName;
        // check if extension exists. Handles cases with no extension and files with '.' at the start of the name.
        const ext = fileName.slice(
            Math.max(0, fileName.lastIndexOf('.')) || Infinity
        );
        // remove the extension to get clean filename for accessiblity alt text lookup
        if (ext) {
            fileName = fileName.replace(ext, '');
        }
        return fileName;
    };

    altText = (originalFileName: string, context?: string) => {
        let altText = '';
        if (originalFileName) {
            const fileName = this.removeFileNameExtension(originalFileName);
            if (fileName) {
                if (this.icons && this.icons[fileName]) {
                    altText += this.icons[fileName];
                } else {
                    altText += sentenceCase(fileName);
                }
            }
        }
        if (context) {
            altText += ` ${context}`;
        }
        return altText;
    };
}



// WEBPACK FOOTER //
// ./src/stores/AccessibilityStore.ts